import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { mediaUrl } from "@/lib/api";
import { ZAR, useCart } from "@/lib/cart";
import { Plus, Sparkle } from "@phosphor-icons/react";

/**
 * Cheap-adds strip: shows the 3 lowest-price products NOT already in cart with
 * a one-click "+ Add" button. Uses the same cart store so it hydrates the order
 * summary live.
 */
export default function CheckoutCrossSell() {
  const cart = useCart();
  const [picks, setPicks] = useState([]);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/products", { params: { limit: 60 } })
      .then((r) => {
        if (cancelled) return;
        const inCart = new Set(cart.items.map((i) => i.product_slug));
        const cheapest = r.data
          .filter((p) => !inCart.has(p.slug) && p.stock > 0)
          .sort((a, b) => a.price - b.price)
          .slice(0, 3);
        setPicks(cheapest);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!picks.length) return null;

  const add = (p) => {
    // Auto-pick the first value for each variant option so this stays a 1-click add.
    let variant = null;
    if (p.variant_options?.length) {
      variant = {};
      p.variant_options.forEach((opt) => {
        variant[opt.name] = opt.values?.[0];
      });
    }
    cart.add({
      product_slug: p.slug,
      name: p.name,
      unit_price: p.price,
      quantity: 1,
      variant,
      image: p.images?.[0] || null,
    });
    toast.success("Added to your order", { description: p.name });
    setPicks((prev) => prev.filter((x) => x.slug !== p.slug));
  };

  return (
    <div
      data-testid="checkout-crosssell"
      className="border border-white/10 bg-[#0d0d0d]"
    >
      <div className="flex items-center gap-2 border-b border-white/10 px-4 py-2.5">
        <Sparkle size={12} weight="fill" className="text-[#FFC900]" />
        <span className="font-label text-[10px] tracking-widest text-[#FFC900]">
          ADD BEFORE YOU PAY
        </span>
      </div>
      <div className="divide-y divide-white/5">
        {picks.map((p) => (
          <div key={p.slug} className="flex items-center gap-3 p-3 sm:p-4">
            <div className="h-14 w-14 shrink-0 overflow-hidden border border-white/10 bg-black">
              {p.images?.[0] ? (
                <img
                  src={mediaUrl(p.images[0])}
                  alt=""
                  className="h-full w-full object-cover"
                />
              ) : null}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-label text-[9px] text-white/40 truncate">{p.brand}</div>
              <div className="mt-0.5 font-display text-[13px] font-bold text-white line-clamp-1">
                {p.name}
              </div>
              <div className="text-[11px] text-[#00FFC2] font-bold">{ZAR(p.price)}</div>
            </div>
            <button
              type="button"
              data-testid={`crosssell-add-${p.slug}`}
              onClick={() => add(p)}
              className="inline-flex items-center gap-1 border border-[#FF5A1F] bg-[#FF5A1F]/10 px-3 py-2 font-label text-[10px] text-[#FF5A1F] hover:bg-[#FF5A1F] hover:text-black transition-colors"
            >
              <Plus size={11} weight="bold" /> ADD
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
